import { useMemo } from 'react'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { collectResults } from 'utils/query'
import dayjs from 'dayjs'
import { useAuth } from 'hooks/UseAuth'
import { INTEGRATION_PRIORITY_ORDER, getProviderForTactic, getIntegrationConfig } from './integrationConfig'

type ScheduleContentKey =
  | 'instagram_contents'
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'email_contents'
  | 'seo_contents'
  | 'tiktok_contents'

// users created after this date will see blog integrations [BLZ-8308]
const BLOG_CONTENT_CUTOFF_DATE = '2025-09-22'

const INTEGRATION_CONTENT_MAP: ReadonlyArray<{ key: ScheduleContentKey; integration: string }> = [
  { key: 'instagram_contents', integration: 'Instagram' },
  { key: 'facebook_contents', integration: 'Facebook' },
  { key: 'linkedin_contents', integration: 'LinkedIn' },
  { key: 'twitter_contents', integration: 'Twitter' },
  { key: 'email_contents', integration: 'Email' },
  { key: 'seo_contents', integration: 'SEO' },
  { key: 'tiktok_contents', integration: 'TikTok' },
] as const

/**
 * Returns which providers are required by brand plan schedules but not connected.
 * Extracted from IntegrationsBanner.tsx for reuse.
 */
export const useMissingRequiredProviders = () => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: externalIntegrationsData } = useExternalIntegrations()
  const auth = useAuth()

  const schedulesQuery = useBrandPlanSchedules(
    brandPlan?.id
      ? {
          brandPlanId: brandPlan.id,
          start_date: dayjs().format('YYYY-MM-DD'),
        }
      : { brandPlanId: -1, start_date: '' },
    { enabled: !!brandPlan?.id },
  )
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)

  const shouldShowBlogContent = useMemo(() => {
    if (!auth?.created_at) return false
    return dayjs(auth.created_at).isAfter(dayjs(BLOG_CONTENT_CUTOFF_DATE))
  }, [auth?.created_at])

  const integrationsFromSchedules = useMemo(() => {
    if (schedules.length === 0) return []

    const integrations = schedules.flatMap((schedule) =>
      INTEGRATION_CONTENT_MAP.filter(({ key }) => {
        if (!shouldShowBlogContent && (key === 'email_contents' || key === 'seo_contents')) {
          return false
        }

        const contents = schedule[key] as Array<{ count: number }> | undefined
        return contents?.some((content) => content.count > 0)
      }).map(({ integration }) => integration),
    )

    return [...new Set(integrations)]
  }, [schedules, shouldShowBlogContent])

  const integrationStatus = useMemo(() => {
    if (!brandPlan || !externalIntegrationsData || integrationsFromSchedules.length === 0) {
      return { missingIntegrations: [], connectedCount: 0, totalCount: 0 }
    }

    const connectedProviders = new Set(
      externalIntegrationsData.external_integrations.map((integration) => integration.provider),
    )

    const implementedIntegrations = integrationsFromSchedules.filter(
      (integration) => getIntegrationConfig(integration, brandPlan.selected_blog_platform)?.isImplemented,
    )

    const isIntegrationConnected = (integration: string): boolean => {
      const requiredProvider = getProviderForTactic(integration, brandPlan.selected_blog_platform)
      return Boolean(requiredProvider && connectedProviders.has(requiredProvider))
    }

    const missingIntegrations = implementedIntegrations.filter((integration) => !isIntegrationConnected(integration))
    const totalCount = implementedIntegrations.length
    const connectedCount = totalCount - missingIntegrations.length

    return { missingIntegrations, connectedCount, totalCount }
  }, [brandPlan, externalIntegrationsData, integrationsFromSchedules])

  const nextIntegration = INTEGRATION_PRIORITY_ORDER.find((integration) =>
    integrationStatus.missingIntegrations.includes(integration),
  )

  return {
    ...integrationStatus,
    integrationsFromSchedules,
    nextIntegration,
    hasMissingProviders: integrationStatus.missingIntegrations.length > 0,
  }
}
